import type { NUploader } from '../interface';

export function useRequest() {
  function getError(options: NUploader.IUploadRequestOption, xhr: XMLHttpRequest) {
    const msg = `fail to ${options.method} ${options.action} ${xhr.status}'`;
    const error = new Error(msg) as NUploader.IUploadRequestError;

    error.status = xhr.status;
    error.method = options.method;
    error.url = options.action as string;

    return error;
  }

  function getBody(xhr: XMLHttpRequest) {
    const text = xhr.responseText || xhr.response;

    if (!text) return text;

    try {
      return JSON.parse(text);
    } catch (err) {
      return text;
    }
  }

  return function upload(option: NUploader.IUploadRequestOption) {
    const xhr = new XMLHttpRequest();

    if (option.onProgress && xhr.upload) {
      xhr.upload.onprogress = function progress(event: NUploader.IUploadProgressEvent) {
        if (event.total! > 0) {
          event.percent = (event.loaded! / event.total!) * 100;
        }
        option.onProgress!(event);
      };
    }

    const formData = new FormData();

    if (option.data) {
      Object.keys(option.data).forEach((key) => {
        const value = option.data![key];

        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
          return;
        }

        formData.append(key, value as string | Blob);
      });
    }

    if (option.file instanceof Blob) {
      formData.append(option.filename || 'file', option.file, (option.file as any).name);
    } else {
      formData.append(option.filename || 'file', option.file!);
    }

    xhr.onerror = function error(e) {
      option.onError!(e);
    };

    xhr.onload = function onload() {
      if (xhr.status < 200 || xhr.status >= 300) {
        return option.onError!(getError(option, xhr), getBody(xhr));
      }

      return option.onSuccess!(getBody(xhr), xhr);
    };

    xhr.open(option.method || 'post', option.action!, true);

    if (option.withCredentials && 'withCredentials' in xhr) {
      xhr.withCredentials = true;
    }

    const headers = option.headers || {};

    if (headers['X-Requested-With'] !== null) {
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    }

    Object.keys(headers).forEach((key) => {
      if (headers[key] !== null) {
        xhr.setRequestHeader(key, headers[key]);
      }
    });

    xhr.send(formData);

    return {
      abort() {
        xhr.abort();
      }
    };
  };
}
